import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

import "./App.css";
import ContactUsPage from "./Component/ContactUsPage";
import HomePage from "./Component/HomePage";
import AdminPage from "./Component/Admin/AdminPage";
import AboutUsPage from "./Component/AboutUsPage";
import PrivacyPolicyPage from "./Component/PrivacyPolicyPage";
import AdminHomePage from "./Component/Admin/AdminHomePage";
import AppDescriptionPage from "./Component/AppDescriptionPage";
import HostVenuePage from "./Component/HostVenuePage";
import WorkForUsPage from "./Component/WorkForUsPage";
import TopNavigationBar from "./Component/TopNavigationBar";
import ScrollToTop from "./Component/ScrollToTop";
import LatestNewsPage from "./Component/LatestNewsPage";
import ShopPage from "./Component/ShopPage";
import PhotoPage from "./Component/PhotoPage";
import Footer from "./Component/Footer";
import GetAPLPage from "./Component/GetAPLPage";
import WebPolicyPage from "./Component/WebPolicyPage";
import ComingSoonPage from "./Component/ComingSoonPage";
import { loadConfig, isComingSoon } from "./utils/config";

// Function to check if user is authenticated by presence of access token
function isAuthenticated() {
  return !!localStorage.getItem("adminAccessToken");
}

// ProtectedRoute wrapper
function ProtectedRoute({ children }) {
  return isAuthenticated() ? children : <Navigate to="/onedot" replace />;
}

// SEO and robots meta tag handler
function SEOManager() {
  const location = useLocation();

  useEffect(() => {
    // Remove existing robots meta tag
    let existingRobots = document.querySelector('meta[name="robots"]');
    if (existingRobots) {
      existingRobots.remove();
    }

    // Check if current path is admin related
    const isAdminPage = location.pathname.startsWith("/onedot");

    if (isAdminPage) {
      // Add noindex, nofollow for admin pages
      const robotsMeta = document.createElement("meta");
      robotsMeta.name = "robots";
      robotsMeta.content = "noindex, nofollow";
      document.head.appendChild(robotsMeta);
    } else {
      // Add normal indexing for public pages
      const robotsMeta = document.createElement("meta");
      robotsMeta.name = "robots";
      robotsMeta.content = "index, follow";
      document.head.appendChild(robotsMeta);
    }

    // Update page title based on route
    const titles = {
      "/": "World Series Of Mahjong - Australia's Premier Mahjong Tournament",
      "/contact": "Contact Us - World Series Of Mahjong",
      "/about": "About Us - World Series Of Mahjong",
      "/policy": "Privacy Policy - World Series Of Mahjong",
    };

    document.title = titles[location.pathname] || titles["/"];
  }, [location.pathname]);

  return null;
}

function PublicPageManager() {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        await loadConfig();

        // Check URL parameter first
        const urlComingSoon = searchParams.get("comingSoon");
        if (urlComingSoon !== null) {
          // URL parameter takes priority
          setShowComingSoon(urlComingSoon === "true");
        } else {
          // Fall back to config
          setShowComingSoon(isComingSoon());
        }
      } catch (error) {
        console.error("Failed to load config:", error);
        // Check URL parameter even if config fails
        const urlComingSoon = searchParams.get("comingSoon");
        if (urlComingSoon !== null) {
          setShowComingSoon(urlComingSoon === "true");
        } else {
          setShowComingSoon(false); // Default to normal site if config fails
        }
      } finally {
        setIsConfigLoaded(true);
      }
    };

    initializeConfig();
  }, [searchParams]);

  // Show loading while config is being loaded
  if (!isConfigLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  // Show Coming Soon page if comingSoon is true
  if (showComingSoon) {
    return <ComingSoonPage />;
  }

  const publicPages = (
    <div>
      <TopNavigationBar />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/web-policy" element={<WebPolicyPage />} />
        <Route path="/wsom-app" element={<AppDescriptionPage />} />
        <Route path="/become-host" element={<HostVenuePage />} />
        <Route path="/work-for-us" element={<WorkForUsPage />} />
        <Route path="/latest-news" element={<LatestNewsPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/photo" element={<PhotoPage />} />
        <Route path="/get-apl" element={<GetAPLPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </div>
  );

  return publicPages;
}

function App() {
  return (
    <Router>
      <SEOManager />
      <Routes>
        <Route path="/onedot" element={<AdminPage />} />
        <Route
          path="/onedot-home"
          element={
            <ProtectedRoute>
              <AdminHomePage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<PublicPageManager />} />
      </Routes>
    </Router>
  );
}

export default App;
